import {
  applyClientNotificationFilters,
  type NotificationFiltersState,
  usesUnreadNotificationsEndpoint,
} from "@/features/notifications/shared/notificationFilters";
import { filterNotificationsVisibleToRole } from "@/features/notifications/utils/notification-visibility";
import { parseNumericLastSelectedId } from "@/lib/pagination/scrollPageUrl";
import { createAuthenticatedApi } from "@/lib/utils";
import type { GetNotificationResponse } from "@/openapi/generated";
import { NotificationsApi } from "@/openapi/generated";

const FETCH_PAGE_SIZE = 100;
const MAX_PAGES = 200;

export interface FetchAllMatchingNotificationIdsParams {
  accessToken: string;
  userId: number;
  filters: NotificationFiltersState;
  quickSearch: string;
  activeRole?: string | null;
}

function fetchNotificationPage(
  accessToken: string,
  userId: number,
  filters: NotificationFiltersState,
  page: number,
  lastSelectedId?: number,
) {
  const service = createAuthenticatedApi(NotificationsApi, accessToken);
  const useUnread = usesUnreadNotificationsEndpoint(filters);
  const cursor = parseNumericLastSelectedId(lastSelectedId);

  return useUnread
    ? service.apiNotificationsUserIdUnreadGet({
        userId,
        page,
        pageSize: FETCH_PAGE_SIZE,
        lastSelectedId: cursor,
      })
    : service.apiNotificationsUserIdGet({
        userId,
        page,
        pageSize: FETCH_PAGE_SIZE,
        lastSelectedId: cursor,
      });
}

/**
 * Walks paginated notification responses and returns items matching the current
 * filters (including client-only read status, type, and title/message search).
 */
export async function fetchAllMatchingNotifications({
  accessToken,
  userId,
  filters,
  quickSearch,
  activeRole,
}: FetchAllMatchingNotificationIdsParams): Promise<GetNotificationResponse[]> {
  const collected: GetNotificationResponse[] = [];
  const seenIds = new Set<string>();
  let page = 1;
  let lastSelectedId: number | undefined;

  while (page <= MAX_PAGES) {
    const data = await fetchNotificationPage(
      accessToken,
      userId,
      filters,
      page,
      lastSelectedId,
    );

    const items = filterNotificationsVisibleToRole(
      applyClientNotificationFilters(data.items ?? [], quickSearch, filters),
      activeRole,
    );

    for (const item of items) {
      if (!item.id || seenIds.has(item.id)) continue;
      seenIds.add(item.id);
      collected.push(item);
    }

    const rawItems = data.items ?? [];
    if (rawItems.length === 0) break;
    if (data.hasNextPage === false) break;

    lastSelectedId = parseNumericLastSelectedId(data.lastSelectedId);
    page += 1;
  }

  return collected;
}

/**
 * Walks paginated notification responses and returns IDs matching the current
 * filters (including client-only title/message search).
 */
export async function fetchAllMatchingNotificationIds(
  params: FetchAllMatchingNotificationIdsParams,
): Promise<string[]> {
  const notifications = await fetchAllMatchingNotifications(params);
  return notifications.map((item) => item.id).filter(Boolean);
}

/** Loads every unread notification ID for "Mark all as read". */
export async function fetchAllUnreadNotificationIds(
  accessToken: string,
  userId: number,
  activeRole?: string | null,
): Promise<string[]> {
  return fetchAllMatchingNotificationIds({
    accessToken,
    userId,
    filters: { readStatus: "unread", notificationType: "" },
    quickSearch: "",
    activeRole,
  });
}
